import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search, BookOpen, GraduationCap, Users, FileText, MessageCircle, Mic, DollarSign,
  User as UserIcon, ArrowRight, Loader2, Command,
} from "lucide-react";
import api, { fileUrl } from "../lib/api";

const KIND_META = {
  stories: { label: "Stories", icon: BookOpen, color: "#f68b1f", path: (item) => `/story/${item.id}` },
  programs: { label: "Programs", icon: GraduationCap, color: "#7ac143", path: (item) => `/programs?id=${item.id}` },
  facilitators: { label: "Facilitators", icon: Users, color: "#0091be", path: (item) => `/facilitators?id=${item.id}` },
  pitches: { label: "Pitches", icon: Mic, color: "#7ac143", path: () => `/pitches` },
  resources: { label: "Resources", icon: FileText, color: "#ffd400", path: () => `/resources` },
  pricing: { label: "Pricing", icon: DollarSign, color: "#083050", path: () => `/pricing` },
};

const KIND_ORDER = ["stories", "programs", "pitches", "facilitators", "resources", "pricing"];

// Single source of truth for the "no results" shape. Used by every reset
// path in the palette so a new kind (e.g. Pricing) can be added by only
// touching KIND_ORDER + KIND_META + this constant.
const EMPTY_RESULTS = KIND_ORDER.reduce((acc, k) => ({ ...acc, [k]: [] }), {});

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Flatten results into a navigable list for keyboard nav
  const flat = useMemo(() => {
    const out = [];
    KIND_ORDER.forEach((kind) => {
      (results[kind] || []).forEach((item) => out.push({ kind, item }));
    });
    return out;
  }, [results]);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults(EMPTY_RESULTS);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (!trimmed) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      api.get("/search", { params: { q: trimmed } })
        .then(({ data }) => {
          setResults({ ...EMPTY_RESULTS, ...(data || {}) });
          setActiveIdx(0);
        })
        .catch(() => setResults(EMPTY_RESULTS))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(timer);
  }, [q, open]);

  const choose = (kind, item) => {
    onClose?.();
    navigate(KIND_META[kind].path(item));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && flat.length > 0) {
      e.preventDefault();
      const target = flat[activeIdx] || flat[0];
      choose(target.kind, target.item);
    }
  };

  if (!open) return null;

  let runningIdx = 0;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 pt-[10vh]"
        data-testid="cmdk-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden"
          data-testid="cmdk-panel"
        >
          <div className="flex items-center gap-3 px-4 sm:px-5 border-b border-slate-100">
            <Search size={18} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              data-testid="cmdk-input"
              placeholder="Search stories, programs, pricing, resources…"
              className="flex-1 h-14 bg-transparent outline-none text-base text-tc-ink placeholder-slate-400"
            />
            {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
            <kbd className="hidden sm:inline-flex h-6 px-1.5 items-center rounded-md bg-slate-100 text-[10px] font-bold text-slate-500 tracking-wider">ESC</kbd>
          </div>

          <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="cmdk-results">
            {flat.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-slate-400">
                {q.trim() ? (loading ? "Searching…" : "No matches. Try fewer or different words.") : (
                  <div className="flex flex-col items-center gap-2">
                    <Command size={20} className="text-slate-300" />
                    <div>Type to search across the whole hub.</div>
                    <div className="text-[11px] text-slate-300">Stories · Programs · Pricing · Pitches · Facilitators · Resources</div>
                  </div>
                )}
              </div>
            ) : (
              KIND_ORDER.map((kind) => {
                const items = results[kind] || [];
                if (items.length === 0) return null;
                const meta = KIND_META[kind];
                const KindIcon = meta.icon;
                return (
                  <div key={kind} className="px-2 pb-2">
                    <div className="px-3 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                      <KindIcon size={11} style={{ color: meta.color }} /> {meta.label}
                    </div>
                    {items.map((item) => {
                      const idx = runningIdx++;
                      const active = idx === activeIdx;
                      return (
                        <button
                          key={`${kind}-${item.id}`}
                          onClick={() => choose(kind, item)}
                          onMouseEnter={() => setActiveIdx(idx)}
                          data-testid={`cmdk-item-${kind}-${item.id}`}
                          className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 transition-colors ${active ? "bg-slate-100" : "hover:bg-slate-50"}`}
                        >
                          <ItemIcon kind={kind} item={item} color={meta.color} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-tc-ink truncate">{item.title}</div>
                            {item.subtitle && (
                              <div className="text-xs text-slate-500 truncate mt-0.5">{item.subtitle}</div>
                            )}
                          </div>
                          {active && <ArrowRight size={14} className="text-slate-400 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-slate-100 px-4 py-2.5 flex items-center justify-between text-[10px] text-slate-400">
            <div className="flex items-center gap-3">
              <span><kbd className="px-1.5 py-0.5 rounded bg-slate-100 font-bold">↑↓</kbd> navigate</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-slate-100 font-bold">↵</kbd> open</span>
            </div>
            <div>Powered by CJ search</div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ItemIcon({ kind, item, color }) {
  if (kind === "facilitators" && item.photo_path) {
    return (
      <img
        src={fileUrl(item.photo_path)}
        alt=""
        className="w-9 h-9 rounded-lg object-cover shrink-0"
      />
    );
  }
  const Icon = kind === "stories"
    ? (item.icon === "client" ? MessageCircle : UserIcon)
    : KIND_META[kind].icon;
  return (
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${color}1f`, color }}
    >
      <Icon size={16} />
    </div>
  );
}
